import { DataSubType } from "oschema";

import {
  Field,
  enumOptions,
  fieldLabel,
  inputKindFor,
  isReadOnlyField,
  isReadOnlyOnCreate,
  isSecretField,
  parsesAsNumber,
} from "./schema-field";

export type FieldErrors = Record<string, string>;

const isBlank = (value: unknown) =>
  value === null || value === undefined || value === "";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const IPV4 = /^(\d{1,3}\.){3}\d{1,3}$/;
const HEX_COLOR = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * Client-side gate before a create/update leaves the browser. It is deliberately
 * forgiving about subtypes the analyser guesses at (URL, phone) and strict only
 * where a wrong value is guaranteed to be rejected or silently corrupt the row.
 */
export const validateRecord = (
  schema: Field[],
  model: Record<string, any>,
  isUpdate?: boolean
): FieldErrors => {
  const errors: FieldErrors = {};

  schema.forEach((field) => {
    const readOnly =
      isUpdate === false ? isReadOnlyOnCreate(field) : isReadOnlyField(field);
    if (readOnly) return;

    const value = model?.[field.name];
    const label = fieldLabel(field);
    const kind = inputKindFor(field);

    if (isBlank(value)) {
      if (isUpdate && isSecretField(field)) return;
      // A boolean toggle is never blank in practice, and a column with a
      // database default does not need a value from the form.
      if (
        field.nullable === false &&
        field.default == null &&
        kind !== "boolean"
      ) {
        errors[field.name] = `${label} is required`;
      }
      return;
    }

    switch (kind) {
      case "number":
      case "currency":
      case "percentage": {
        if (!parsesAsNumber(value)) {
          errors[field.name] = `${label} must be a number`;
          break;
        }
        const n = Number(value);
        // Only complain about decimals when the column really is an integer
        // column, not when the analyser guessed INTEGER for a varchar.
        if (
          field.type === "NUMBER" &&
          field.subType === DataSubType.INTEGER &&
          !Number.isInteger(n)
        ) {
          errors[field.name] = `${label} must be a whole number`;
        }
        break;
      }

      case "json": {
        if (typeof value === "string") {
          try {
            JSON.parse(value);
          } catch {
            errors[field.name] = `${label} is not valid JSON`;
          }
        }
        break;
      }

      case "enum": {
        const options = enumOptions(field);
        if (
          options &&
          field.enumClosed === true &&
          !options.includes(String(value))
        ) {
          errors[field.name] = "Pick one of the listed values";
        }
        break;
      }

      case "email":
        if (!EMAIL.test(String(value))) {
          errors[field.name] = `${label} is not a valid email address`;
        }
        break;

      case "color":
        if (!HEX_COLOR.test(String(value).trim())) {
          errors[field.name] = `${label} must be a hex colour like #4f46e5`;
        }
        break;

      case "date":
      case "datetime":
      case "time":
        // Only a column the analyser typed DATE outright is held to this. A
        // STRING column it merely guessed a temporal subType for (it has typed
        // a numeric budget column TIMESTAMP) would otherwise be unsaveable.
        if (
          field.type === "DATE" &&
          isNaN(new Date(normaliseTemporal(value, kind)).getTime())
        ) {
          errors[field.name] = `${label} is not a valid ${kind}`;
        }
        break;

      default:
        break;
    }

    if (errors[field.name]) return;

    if (field.subType === DataSubType.UUID && !UUID.test(String(value))) {
      errors[field.name] = `${label} is not a valid UUID`;
    } else if (
      field.subType === DataSubType.IP_ADDRESS &&
      !IPV4.test(String(value)) &&
      !String(value).includes(":")
    ) {
      errors[field.name] = `${label} is not a valid IP address`;
    }
    // Deliberately no maxLength check: `maxLength` is the longest value the
    // analyser happened to sample, not the column's declared width, so
    // enforcing it would reject perfectly valid longer values.
  });

  return errors;
};

const normaliseTemporal = (value: unknown, kind: string) =>
  kind === "time" ? `1970-01-01T${value}` : String(value);

/**
 * Everything the server should not receive: primary keys it generates, audit
 * timestamps it maintains, and (on update) fields the user never touched.
 */
export const buildPayload = (
  schema: Field[],
  model: Record<string, any>,
  original?: Record<string, any> | null
): Record<string, any> => {
  const payload: Record<string, any> = {};
  const isUpdate = !!original && Object.keys(original).length > 0;

  schema.forEach((field) => {
    const readOnly = isUpdate
      ? isReadOnlyField(field)
      : isReadOnlyOnCreate(field);
    if (readOnly) return;
    if (!(field.name in model)) return;

    const next = model[field.name];
    if (isUpdate) {
      if (isSecretField(field) && isBlank(next)) return;
      const before = original?.[field.name];
      if (equivalent(before, next)) return;
    }
    if (field.name.startsWith("_")) return;
    payload[field.name] = normaliseOutgoing(next, field);
  });

  for (const key of Object.keys(payload)) {
    if (key.startsWith("_")) delete payload[key];
  }

  return payload;
};

export const equivalent = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (isBlank(a) && isBlank(b)) return true;
  if (typeof a === "object" || typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return String(a) === String(b);
};

/**
 * Fields the user actually moved off the seeded form. Create payload still
 * includes column defaults; this list is only for dirty chrome and the
 * discard confirm.
 */
export const dirtyFieldNames = (
  schema: Field[],
  model: Record<string, any>,
  baseline: Record<string, any> | null | undefined,
  isUpdate: boolean
): string[] => {
  const base = baseline ?? {};
  return schema
    .filter((field) =>
      isUpdate ? !isReadOnlyField(field) : !isReadOnlyOnCreate(field)
    )
    .filter((field) => !equivalent(model?.[field.name], base[field.name]))
    .map((field) => field.name);
};

const normaliseOutgoing = (value: unknown, field: Field): unknown => {
  if (value === "") return field.nullable === false ? "" : null;
  if (inputKindFor(field) === "json" && typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};
